import axios from 'axios'
import { apiBaseUrl, ecommerceApiBaseUrl, jarvisApiBaseUrl } from '@/config'
import { consoleLog, consoleError, consoleWarn } from '@/utils/logger'


const DEFAULT_API_BASE_URL = `${ecommerceApiBaseUrl}/api`

class AdminService {
  constructor() {
    // Default instance for Ecommerce API (chatbot-owners, whatsapp-accounts)
    this.api = axios.create({
      baseURL: DEFAULT_API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json'
      }
    })
    
    // Interceptor pour ajouter le token d'authentification
    this.api.interceptors.request.use((config) => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        const token = user.data?.token || localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch (e) {
        consoleWarn('Impossible de récupérer le token:', e)
      }
      return config
    })

    // Secondary instance for PeeloChat API (Global Stats, Admin Config)
    this.peeloApi = axios.create({
      baseURL: apiBaseUrl, // No /api suffix for dashboard.js endpoints usually, but let's verify if they are prefixed. 
                          // Looking at dashboard.js loops: app.get('/admin/global-stats'). 
                          // apiBaseUrl = https://api.peelo.io.
                          // So call should be https://api.peelo.io/admin/global-stats
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    this.peeloApi.interceptors.request.use((config) => {
       try {
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        const token = user.data?.token || localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch (e) {
        consoleWarn('Impossible de récupérer le token:', e)
      }
      return config
    })

    // Third instance for Jarvis API (Plans & Subscriptions)
    this.jarvisApi = axios.create({
      baseURL: `${jarvisApiBaseUrl}/api`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    this.jarvisApi.interceptors.request.use((config) => {
       try {
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        const token = user.data?.token || localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch (e) {
        consoleWarn('Impossible de récupérer le token pour Jarvis:', e)
      }
      return config
    })
  }

  // ChatBot Owners API
  async getChatbotOwners(params = {}) {
    try {
      consoleLog('🔍 Appel API getChatbotOwners:', DEFAULT_API_BASE_URL + '/chatbot-owners', params)
      const response = await this.api.get('/chatbot-owners', { params })
      consoleLog('✅ Réponse getChatbotOwners:', response.data)
      return response.data
    } catch (error) {
      consoleError('❌ Erreur lors de la récupération des propriétaires:', error.response?.data || error.message)
      throw error
    }
  }

  async getChatbotOwnerById(ownerId) {
    try {
      const response = await this.api.get(`/chatbot-owners/${ownerId}`)
      return response.data
    } catch (error) {
      consoleError('Erreur lors de la récupération du propriétaire:', error)
      throw error
    }
  }

  async searchChatbotOwners(query, params = {}) {
    try {
      const response = await this.api.get('/chatbot-owners/search', {
        params: { q: query, ...params }
      })
      return response.data
    } catch (error) {
      consoleError('Erreur lors de la recherche:', error)
      throw error
    }
  }

  // WhatsApp Accounts API
  async getWhatsAppAccounts(params = {}) {
    try {
      consoleLog('🔍 Appel API getWhatsAppAccounts:', DEFAULT_API_BASE_URL + '/whatsapp-accounts', params)
      const response = await this.api.get('/whatsapp-accounts', { params })
      consoleLog('✅ Réponse getWhatsAppAccounts:', response.data)
      return response.data
    } catch (error) {
      consoleError('❌ Erreur lors de la récupération des comptes WhatsApp:', error.response?.data || error.message)
      throw error
    }
  }

  // Nouvelle méthode pour récupérer les comptes WhatsApp par owner ID
  async getWhatsAppAccountsByOwner(ownerId) {
    try {
      consoleLog('🔍 Appel API getWhatsAppAccountsByOwner:', DEFAULT_API_BASE_URL + `/whatsapp-accounts/owner/${ownerId}`)
      const response = await this.api.get(`/whatsapp-accounts/owner/${ownerId}`)
      consoleLog('✅ Réponse getWhatsAppAccountsByOwner:', response.data)
      return response.data
    } catch (error) {
      consoleError('❌ Erreur lors de la récupération des comptes WhatsApp par owner:', error.response?.data || error.message)
      throw error
    }
  }

  async getWhatsAppAccountById(accountId) {
    try {
      const response = await this.api.get(`/whatsapp-accounts/${accountId}`)
      return response.data
    } catch (error) {
      consoleError('Erreur lors de la récupération du compte WhatsApp:', error)
      throw error
    }
  }

  async updateWhatsAppAccountStatus(accountId, status) {
    try {
      const response = await this.api.put(`/whatsapp-accounts/${accountId}/status`, {
        status
      })
      return response.data
    } catch (error) {
      consoleError('Erreur lors de la mise à jour du statut:', error)
      throw error
    }
  }

  async updateWhatsAppAccount(accountId, data) {
    try {
      const response = await this.api.put(`/whatsapp-accounts/${accountId}`, data)
      return response.data
    } catch (error) {
      consoleError('Erreur lors de la mise à jour du compte WhatsApp:', error)
      throw error
    }
  }

  // PeeloShop API
  async getPeeloShops(params = {}) {
    try {
      const response = await this.api.get('/peelo-shops', { params })
      return response.data
    } catch (error) {
      consoleError('Erreur lors de la récupération des boutiques:', error)
      throw error
    }
  }

  async getPeeloShopById(shopId) {
    try {
      const response = await this.api.get(`/peelo-shops/${shopId}`)
      return response.data
    } catch (error) {
      consoleError('Erreur lors de la récupération de la boutique:', error)
      throw error
    }
  }

  // Méthodes utilitaires
  async getOwnerWithWhatsAppAccounts(ownerId) {
    try {
      const [ownerResponse, whatsappResponse] = await Promise.all([
        this.getChatbotOwnerById(ownerId),
        this.getWhatsAppAccounts({ ownerId })
      ])

      return {
        owner: ownerResponse.owner,
        whatsappAccounts: whatsappResponse.accounts || []
      }
    } catch (error) {
      consoleError('Erreur lors de la récupération des données combinées:', error)
      throw error
    }
  }

  async getAllOwnersWithAccounts(params = {}) {
    try {
      const ownersResponse = await this.getChatbotOwners(params)
      const owners = ownersResponse.owners || []

      // Récupérer les comptes WhatsApp pour chaque propriétaire avec le bon endpoint
      const ownersWithAccounts = await Promise.all(
        owners.map(async (owner) => {
          try {
            consoleLog(`🔍 Récupération des comptes WhatsApp pour owner: ${owner._id}`)
            const whatsappResponse = await this.getWhatsAppAccountsByOwner(owner._id)
            
            // 🔍 DEBUG: Examiner la réponse pour cet owner
            consoleLog(`📱 DEBUG WhatsApp pour ${owner.name} (${owner._id}):`, {
              success: whatsappResponse.success,
              dataLength: whatsappResponse.data?.length || 0,
              count: whatsappResponse.count,
              ownerId: whatsappResponse.ownerId,
              fullResponse: whatsappResponse
            })
            
            return {
              ...owner,
              whatsappAccounts: whatsappResponse.data || []
            }
          } catch (error) {
            consoleWarn(`❌ Erreur pour le propriétaire ${owner._id}:`, error)
            return {
              ...owner,
              whatsappAccounts: []
            }
          }
        })
      )

      return {
        ...ownersResponse,
        owners: ownersWithAccounts
      }
    } catch (error) {
      consoleError('Erreur lors de la récupération de tous les propriétaires:', error)
      throw error
    }
  }

  // Configuration Admin (USES peeloApi which points to api.peelo.io)
  async getGlobalStats() {
    try {
      // NOTE: peeloApi has baseURL = https://api.peelo.io
      const response = await this.peeloApi.get('/admin/global-stats')
      return response.data
    } catch (error) {
      consoleError('Erreur getGlobalStats:', error)
      throw error
    }
  }

  async getAdminConfig(type) {
    try {
      const response = await this.peeloApi.get(`/admin/config/${type}`)
      return response.data
    } catch (error) {
      consoleError('Erreur getAdminConfig:', error)
      throw error
    }
  }

  async updateAdminConfig(type, data) {
    try {
      const response = await this.peeloApi.post(`/admin/config/${type}`, data)
      return response.data
    } catch (error) {
      consoleError('Erreur updateAdminConfig:', error)
      throw error
    }
  }

  // Jarvis API (Plans & Subscriptions)
  async getPlans() {
    try {
      const response = await this.jarvisApi.get('/plans')
      return response.data
    } catch (error) {
      consoleError('Erreur getPlans:', error)
      throw error
    }
  }

  async assignPlan(ownerId, planSlug, customPrice = null) {
    try {
      const payload = {
        owner_id: ownerId,
        plan_slug: planSlug
      }

      if (customPrice !== null) {
        payload.custom_price_usd = parseFloat(customPrice)
      }

      const response = await this.jarvisApi.put('/subscription/assign-plan', payload)
      return response.data
    } catch (error) {
      consoleError('Erreur assignPlan:', error)
      throw error
    }
  }

  // Credits & Monitoring API (Jarvis)
  async getGlobalCreditsDashboard(month) {
    try {
      const params = month ? { month } : {}
      const response = await this.jarvisApi.get('/credits/global/dashboard', { params })
      return response.data
    } catch (error) {
      consoleError('Erreur getGlobalCreditsDashboard:', error)
      throw error
    }
  }

  async setGlobalCreditsBudget(data) {
    try {
      const response = await this.jarvisApi.post('/credits/global/set-budget', data)
      return response.data
    } catch (error) {
      consoleError('Erreur setGlobalCreditsBudget:', error)
      throw error
    }
  }

  async testGlobalCreditsWebhook() {
    try {
      const response = await this.jarvisApi.post('/credits/global/test-webhook')
      return response.data
    } catch (error) {
      consoleError('Erreur testGlobalCreditsWebhook:', error)
      throw error
    }
  }

  // ===== Quotas IA (superadmin) =====

  // Quota de base par plan (Moo / Griot) — réutilise PUT /plans/:slug
  async updatePlanQuota(slug, { ai_quota_moo, ai_quota_griot }) {
    try {
      const response = await this.jarvisApi.put(`/plans/${slug}`, { ai_quota_moo, ai_quota_griot })
      return response.data
    } catch (error) {
      consoleError('Erreur updatePlanQuota:', error)
      throw error
    }
  }

  // Override compte (owner) : { ai_unlimited, ai_quota_override: { moo, griot } }
  async setOwnerAiQuota(ownerId, payload) {
    try {
      const response = await this.jarvisApi.put(`/admin/owner/${ownerId}/ai-quota`, payload)
      return response.data
    } catch (error) {
      consoleError('Erreur setOwnerAiQuota:', error)
      throw error
    }
  }

  // Override agent (chatbot) : { unlimited, moo, griot }
  async setAgentAiQuota(idbot, payload) {
    try {
      const response = await this.jarvisApi.put(`/admin/agent/${idbot}/ai-quota`, payload)
      return response.data
    } catch (error) {
      consoleError('Erreur setAgentAiQuota:', error)
      throw error
    }
  }

  // Liste des agents d'un owner + leur ai_quota
  async getOwnerAgents(ownerId) {
    try {
      const response = await this.jarvisApi.get(`/admin/owner/${ownerId}/agents`)
      return response.data
    } catch (error) {
      consoleError('Erreur getOwnerAgents:', error)
      throw error
    }
  }
}

export default new AdminService()
